/**
 * Conflicts API Hook
 * Provides data fetching and mutation for planning conflicts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';

const API_BASE = '/planning';

// Conflict severity levels
export type ConflictSeverity = 'info' | 'warning' | 'error';

// Conflict types
export type ConflictType =
  | 'capacity_overload'
  | 'late_delivery'
  | 'material_shortage'
  | 'resource_conflict'
  | 'sequence_violation'
  | 'employee_unavailable'
  | 'maintenance_conflict';

// Conflict data structure (matches backend serializer)
export interface PlanningConflict {
  id: string;
  scenario: string;
  severity: ConflictSeverity;
  severity_display: string;
  conflict_type: ConflictType;
  conflict_type_display: string;
  message: string;
  affected_operation: string | null;
  affected_work_center: string | null;
  affected_work_center_code: string | null;
  affected_date: string | null;
  is_resolved: boolean;
  resolution_note: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
}

// Conflict summary by type/severity
export interface ConflictSummary {
  total: number;
  unresolved: number;
  bySeverity: {
    error: number;
    warning: number;
    info: number;
  };
  byType: Partial<Record<ConflictType, number>>;
}

// Fetch conflicts for a scenario
async function fetchConflicts(
  scenarioId: string,
  showResolved = false
): Promise<PlanningConflict[]> {
  const params = new URLSearchParams();
  if (showResolved) params.append('show_resolved', 'true');

  const response = await api.get(`${API_BASE}/scenarios/${scenarioId}/conflicts/?${params}`);
  return response.data;
}

// Resolve a conflict
async function resolveConflict(
  conflictId: string,
  resolutionNote: string
): Promise<{ id: string; is_resolved: boolean; message: string }> {
  const response = await api.post(
    `${API_BASE}/conflicts/${conflictId}/resolve/`,
    { resolution_note: resolutionNote }
  );
  return response.data;
}

// Hook: Fetch conflicts for a scenario
export function useConflicts(
  scenarioId: string | undefined,
  showResolved = false,
  enabled = true
) {
  return useQuery({
    queryKey: ['conflicts', scenarioId, showResolved],
    queryFn: () => fetchConflicts(scenarioId!, showResolved),
    enabled: enabled && !!scenarioId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

// Hook: Resolve a conflict
export function useResolveConflict() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conflictId, resolutionNote }: { conflictId: string; resolutionNote: string }) =>
      resolveConflict(conflictId, resolutionNote),
    onSuccess: () => {
      // Invalidate conflicts query to trigger refresh
      queryClient.invalidateQueries({ queryKey: ['conflicts'] });
      // Also invalidate scenarios as conflict count affects them
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
    },
  });
}

// Helper: Calculate conflict summary from list
export function calculateConflictSummary(conflicts: PlanningConflict[]): ConflictSummary {
  const summary: ConflictSummary = {
    total: conflicts.length,
    unresolved: conflicts.filter((c) => !c.is_resolved).length,
    bySeverity: { error: 0, warning: 0, info: 0 },
    byType: {},
  };

  for (const conflict of conflicts) {
    if (!conflict.is_resolved) {
      summary.bySeverity[conflict.severity]++;
      summary.byType[conflict.conflict_type] = (summary.byType[conflict.conflict_type] || 0) + 1;
    }
  }

  return summary;
}

// Helper: Get severity color
export function getSeverityColor(severity: ConflictSeverity): {
  bg: string;
  text: string;
  border: string;
  icon: string;
} {
  switch (severity) {
    case 'error':
      return {
        bg: 'bg-red-50',
        text: 'text-red-700',
        border: 'border-red-200',
        icon: 'text-red-500',
      };
    case 'warning':
      return {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200',
        icon: 'text-amber-500',
      };
    case 'info':
    default:
      return {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        icon: 'text-blue-500',
      };
  }
}

// Helper: Get conflict type icon name
export function getConflictTypeIcon(type: ConflictType): string {
  switch (type) {
    case 'capacity_overload':
      return 'gauge';
    case 'late_delivery':
      return 'clock';
    case 'material_shortage':
      return 'package-x';
    case 'resource_conflict':
      return 'users';
    case 'sequence_violation':
      return 'arrow-right-left';
    case 'employee_unavailable':
      return 'user-x';
    case 'maintenance_conflict':
      return 'wrench';
    default:
      return 'alert-triangle';
  }
}

// Helper: Sort conflicts by priority
export function sortConflictsByPriority(conflicts: PlanningConflict[]): PlanningConflict[] {
  const severityOrder: Record<ConflictSeverity, number> = {
    error: 0,
    warning: 1,
    info: 2,
  };

  return [...conflicts].sort((a, b) => {
    // Unresolved first
    if (a.is_resolved !== b.is_resolved) {
      return a.is_resolved ? 1 : -1;
    }
    // Then by severity
    if (severityOrder[a.severity] !== severityOrder[b.severity]) {
      return severityOrder[a.severity] - severityOrder[b.severity];
    }
    // Then by date (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

// Helper: Group conflicts by work center
export function groupConflictsByWorkCenter(
  conflicts: PlanningConflict[]
): Map<string, PlanningConflict[]> {
  const grouped = new Map<string, PlanningConflict[]>();

  for (const conflict of conflicts) {
    const key = conflict.affected_work_center_code || 'Allgemein';
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(conflict);
  }

  return grouped;
}
